import { isValidRussianPhone } from '@/shared/lib/phone';
import { z } from 'zod';
import { ApiDialogActionType, ApiDialogAudienceType, ApiDialogOptionType } from '@/core/generated/api';
import { requiredText, textWithinLimit } from '@/shared/lib/validation';

/** Проверяет содержание шаблона диалогового окна */
export const dialogTemplateSchema = z.object({
    code: requiredText('Код')
        .pipe(textWithinLimit(100))
        .refine(value => /^[a-zA-Z0-9-]+$/.test(value), 'Латиница, цифры и дефис'),
    title: requiredText('Заголовок')
        .pipe(textWithinLimit(100)),
    text: requiredText('Описание')
        .pipe(textWithinLimit(300)),
    action: z.enum(ApiDialogActionType),
    textInput: z.object({
        label: requiredText('Подпись поля')
            .pipe(textWithinLimit(100)),
        placeholder: textWithinLimit(300),
        maxLength: z.number()
            .int()
            .min(1, 'Минимум один символ')
            .max(4000, 'Максимум 4000 символов'),
    })
        .nullable(),
    options: z.array(z.object({
        optionType: z.enum(ApiDialogOptionType),
        text: requiredText('Подпись кнопки')
            .pipe(textWithinLimit(100)),
    }))
        .max(5),
    routeName: z.string()
        .refine(value => !value || /^\/[^\s?#]*$/.test(value), 'Маршрут начинается с /'),
    routeEntityId: z.string()
        .refine(value => !value || z.uuid().safeParse(value).success, 'Укажите UUID объекта'),
    isEnabled: z.boolean(),
})
    .superRefine((value, context) => {
        const feedback = value.action === ApiDialogActionType.CollectFeedback;

        if (feedback !== (value.textInput !== null)) {
            context.addIssue({
                code: 'custom',
                path: ['textInput'],
                message: 'Поле ввода доступно только для сбора обратной связи',
            });
        }

        const validFeedbackButtons = value.options.length === 2
            && value.options[0]?.optionType === ApiDialogOptionType.Continue
            && value.options[1]?.optionType === ApiDialogOptionType.Skip;

        if (feedback && !validFeedbackButtons) {
            context.addIssue({
                code: 'custom',
                path: ['options'],
                message: 'Нужны кнопки «Ответить» и «Пропустить»',
            });
        }

        if (new Set(value.options.map(option => option.optionType)).size !== value.options.length) {
            context.addIssue({
                code: 'custom',
                path: ['options'],
                message: 'Значения кнопок должны различаться',
            });
        }

        if (value.routeEntityId && !value.routeName) {
            context.addIssue({
                code: 'custom',
                path: ['routeName'],
                message: 'Укажите маршрут объекта',
            });
        }

        if (value.action === ApiDialogActionType.RecordAnswer && value.options.length === 0) {
            context.addIssue({
                code: 'custom',
                path: ['options'],
                message: 'Добавьте варианты ответа',
            });
        }
    });

export type DialogTemplateFormValues = z.infer<typeof dialogTemplateSchema>;

/** Проверяет параметры выпуска */
export const dialogBroadcastSchema = z.object({
    templateId: z.uuid('Выберите шаблон'),
    name: requiredText('Название выпуска')
        .pipe(textWithinLimit(150)),
    audience: z.enum(ApiDialogAudienceType),
    cityId: z.number()
        .int()
        .positive()
        .nullable(),
    nameSearch: textWithinLimit(100),
    phoneSearch: textWithinLimit(50)
        .refine(value => !value.trim() || isValidRussianPhone(value), 'Введите полный номер телефона'),
    petNameSearch: textWithinLimit(100),
    activeFrom: z.date({ error: 'Укажите корректную дату и время' })
        .nullable(),
    expiresAt: z.date({ error: 'Укажите корректную дату и время' })
        .nullable(),
})
    .superRefine((value, context) => {
        if (value.expiresAt && value.expiresAt <= new Date()) {
            context.addIssue({
                code: 'custom',
                path: ['expiresAt'],
                message: 'Срок уже истёк',
            });
        }

        if (value.activeFrom && value.expiresAt && value.activeFrom >= value.expiresAt) {
            context.addIssue({
                code: 'custom',
                path: ['expiresAt'],
                message: 'Окончание должно быть после начала',
            });
        }
    });

export type DialogBroadcastFormValues = z.infer<typeof dialogBroadcastSchema>;
